import { graphql } from "./graphql";

export const EstimatesQuery = graphql(`
  query getEstimates {
    estimates {
      id
      number
      title
      createdAt
      date
      requestedAt
      requestedChanges
      total
      subtotal
      tax
      status
    }
  }
`);

export const EstimateQuery = graphql(`
  query getEstimate($estimateId: BigInt!) {
    account {
      fullName
      firstName
      lastName
      phone
      paymentMethodRequired
      hasPaymentMethod
      email
      address {
        city
        country
        state
        street1
        street2
        zip
      }
      credit
    }
    estimate(estimateId: $estimateId) {
      payInFullEnabled
      depositOwed
      depositRequirement
      id
      isArchived
      title
      status
      purchaseOrder
      date
      requestedChanges
      notes
      terms
      subtotal
      tax
      paidAmount
      total
      totalOwed
      createdAt
      updatedAt
      requestedAt
      number
      discountPercent
      discountAmount
      purchaseOrder
      media {
        id
        url
        uploadedAt
        description
        contentType
        image_sm: thumb(width: 257)
        image_lg: thumb(width: 1400)
      }
      customFields {
        id
        name
        type
        textValue
        integerValue
        decimalValue
        dateValue
        dynamicCustomField {
          id
          name
          type
        }
      }
      lineItems {
        id
        name
        description
        cost
        taxPercent1
        taxPercent2
        quantity
        taxPrice1
        taxPrice2
        subtotal
        subtotalWithTax
        removed
        isRecommended
        property {
          id
          name
          address {
            street1
            street2
            city
            state
            zip
          }
        }
      }
    }
  }
`);

export const InvoicesQuery = graphql(`
  query getInvoices {
    invoices {
      id
      number
      title
      createdAt
      date
      total
      subtotal
      tax
      status
      isSent
      paidAmount
      daysPastDue
    }
  }
`);

export const InvoiceQuery = graphql(`
  query getInvoice($invoiceId: SafeInt!) {
    account {
      fullName
      firstName
      lastName
      phone
      paymentMethodRequired
      hasPaymentMethod
      email
      address {
        city
        country
        state
        street1
        street2
        zip
      }
      credit
    }
    invoice(invoiceId: $invoiceId) {
      id
      number
      purchaseOrder
      date
      status
      title
      notes
      terms
      subtotal
      tax
      total
      paidAmount
      contract
      lateFee
      processingFee
      propertyId
      lastUpdated
      lastAutoChargeAttempt
      autoChargedAt
      sentAt
      sorted
      lateFeeRule
      lateFeeRuleVersion
      createdAt
      discountPercent
      discountAmount
      isSent
      deletedState
      lateFee
      daysPastDue
      dueDate
      media {
        id
        url
        uploadedAt
        description
        thumb
        contentType
        image_sm: thumb(width: 257)
        image_lg: thumb(width: 1400)
      }
      customFields {
        id
        name
        type
        textValue
        integerValue
        decimalValue
        dateValue
        dynamicCustomField {
          id
          name
          type
        }
      }
      lineItems(orderBy: [{ lineItemOrder: asc }, { id: asc }]) {
        id
        date
        name
        description
        cost
        quantity
        taxPrice1
        taxPrice2
        taxPercent1
        taxPercent2
        subtotal
        property {
          id
          name
          address {
            street1
            street2
            city
            state
            zip
          }
        }
      }
    }
  }
`);

export const EstimateHistoryQuery = graphql(`
  query getEstimateHistory($estimateId: BigInt!) {
    estimate(estimateId: $estimateId) {
      history {
        id
        userId
        user {
          id
          fullName
        }
        customer {
          id
          fullName
        }
        date
        action
        customerId
        comment
      }
    }
  }
`);

export const InvoiceHistoryQuery = graphql(`
  query getInvoiceHistory($invoiceId: SafeInt!) {
    invoice(invoiceId: $invoiceId) {
      history {
        id
        userId
        user {
          id
          fullName
        }
        customer {
          id
          fullName
        }
        date
        action
        customerId
        comment
        meta
        payment {
          id
          date
          notes
          details
          method
          methodDisplayName
          invoice {
            id
            number
          }
          userId
          tipAmount
          processFeeCharged
          paidAmount
          creditAmount
        }
      }
    }
  }
`);

export const CompanyQuery = graphql(`
  query getCompany {
    company {
      id
      name
      phone
      mobile
      email
      www
      paypalEmail
      isPaypalEnabled
      address {
        street1
        street2
        city
        state
        zip
        country
      }
      logo {
        url
        thumb
      }
      timezone
      settings {
        hideWrf
        showEstimateCostRate
        showInvoiceCostRate
        showEstimateQuantityPerHour
        showInvoiceQuantityPerHour
        showInvoiceBudgetedHours
      }
      showCustomFields
      showAddCredit
      invoiceDueDate
      showTips
      showInvoiceStatus
      hideInvoiceOutstandingBalance
      tier {
        name
        features {
          # hasWorkRequests
          # hasPayInFull
          # hasTipping

          workRequests
          payInFull
          tipping
        }
      }
    }
  }
`);

export const CompanyFeaturesQuery = graphql(`
  query getCompanyFeatures {
    company {
      id
      settings {
        hideWrf
      }
      tier {
        features {
          # hasWorkRequests
          # hasPayInFull
          # hasTipping

          workRequests
          payInFull
          tipping
        }
      }
    }
  }
`);

export const CompanySettingsQuery = graphql(`
  query getCompanySettings {
    company {
      id
      email
      timezone
      currency
      customNaming {
        tax
        taxes
        estimate
        estimates
        invoice
        invoices
      }
    }
  }
`);

export const PublicCompanyQuery = graphql(`
  query getPublicCompanyInfoQuery($companyId: Int!) {
    getPublicCompanyInfo(companyId: $companyId) {
      id
      name
      phone
      mobile
      email
      www
      isBanned
      address {
        street1
        street2
        city
        state
        zip
        country
      }
      logo {
        url
        thumb
      }
      timezone
    }
  }
`);

export const StripeQuery = graphql(`
  query findStripeAccount {
    stripe {
      id
      stripeAccountId
    }
  }
`);

export const AnalyticsQuery = graphql(`
  query getCustomerInfo {
    account {
      id
      fullName
      email
    }
    company {
      id
      name
    }
  }
`);

export const PaymentQuery = graphql(`
  query getPayment($paymentId: SafeInt!) {
    payment(paymentId: $paymentId) {
      id
      date
      notes
      details
      method
      invoice {
        id
        number
      }
      userId
      tipAmount
      processFeeCharged
      paidAmount
      creditAmount
    }
  }
`);

export const CustomerPaymentHistoryQuery = graphql(`
  query getPayments {
    payments {
      id
      date
      notes
      details
      method
      invoice {
        id
        number
      }
      userId
      tipAmount
      processFeeCharged
      paidAmount
      creditAmount
    }
  }
`);

export const StripePaymentMethodsQuery = graphql(`
  query getStripePaymentMethods {
    stripePaymentMethods {
      id
      object
      type
      metadata
      card
      us_bank_account
      status
      verificationMethod
    }
  }
`);

export const CustomerQuery = graphql(`
  query getCustomer {
    account {
      id
      fullName
      firstName
      lastName
      phone
      cell
      email
      portalNeedsLogin
      loginEmail
      receiveMarketingEmails
      pastDue
      outstanding
      paymentMethodRequired
      hasPaymentMethod
      credit
      invoiceDueDate
      showTips
      hideInvoiceOutstandingBalance
      showInvoiceStatus
      address {
        street1
        street2
        city
        state
        zip
        country
      }
      properties {
        id
        name
        address {
          street1
          street2
          city
          state
          zip
        }
      }
    }
  }
`);

export const CustomerEventsQuery = graphql(`
  query getCustomerEvents($filter: EventFilter) {
    events(where: $filter, orderBy: { startDateTime: desc }) {
      id
      title
      status
      type
      startDate
      invoice {
        id
        number
        isSent
        deletedState
      }
      property {
        address {
          street1
          street2
        }
      }
    }
  }
`);

export const EventsWithMediaQuery = graphql(`
  query getEventsWithMedia($where: EventFilter) {
    events(where: $where, orderBy: { startDateTime: desc }) {
      id
      title
      description
      startDate
      media {
        ...ThumbnailFields
      }
      beforeAndAfters {
        id
        beforeDescription
        afterDescription
        isClientVisible
        beforeMedia {
          ...ThumbnailFields
        }
        afterMedia {
          ...ThumbnailFields
        }
      }
    }
  }

  fragment ThumbnailFields on Media {
    id
    url
    uploadedAt
    contentType
    description
    image_sm: thumb(width: 257)
    image_lg: thumb(width: 1400)
  }
`);

export const CustomerRatingsQuery = graphql(`
  query getCustomerRatings {
    ratings {
      id
      value
      note
      createdAt
      serviceName
    }
  }
`);

export const CustomerDocumentsQuery = graphql(`
  query documents {
    documents {
      id
      companyId
      description
      text
      date
      customerId
      name
      isArchived
      sentEmail
      snailMail
      view
    }
  }
`);

export const CustomerDocumentQuery = graphql(`
  query document($documentId: SafeInt!) {
    document(documentId: $documentId) {
      id
      companyId
      description
      text
      date
      customerId
      name
      isArchived
      sentEmail
      snailMail
      view
    }
  }
`);

export const CustomerMediasQuery = graphql(`
  query medias {
    medias {
      url
      uploadedAt
      ...ThumbnailFields
    }
  }

  fragment ThumbnailFields on Media {
    id
    contentType
    description
    image_sm: thumb(width: 257)
    image_lg: thumb(width: 1400)
  }
`);

export const WorkRequestsQuery = graphql(`
  query getWorkRequests {
    workRequests {
      id
      address {
        street1
        street2
        city
        state
        zip
      }
      summary
      appointmentTimes
      createdAt
    }
  }
`);

export const WorkRequestQuery = graphql(`
  query getWorkRequest($workRequestId: SafeInt!) {
    workRequest(workRequestId: $workRequestId) {
      id
      address {
        street1
        street2
        city
        state
        zip
      }
      summary
      details
      frequency
      preferredDate1
      preferredDate2
      appointmentTimes
      createdAt
    }
  }
`);
